import { moduleSimulationGraph } from "./simulation/moduleSimulationGraph";
import { jsonCopy, getObjectSize } from "../utils/helper";
import {
  getRangeAsOffset,
  getSheetNameFromAddress,
  getDataGridFromRange,
  removeSheetProperties,
} from "../utils/helperExcel";

export class StoreCalculation {
  constructor(rootStore) {
    this.rootStore = rootStore;
    this.moduleGraph = new moduleSimulationGraph(this);
  }

  //==========================
  // SESSIONS
  //==========================

  async useSessionPing(elementInfo, errorCallback) {
    await this.moduleGraph.useSessionPing(elementInfo, errorCallback);
  }

  async closeSession(elementInfo) {
    this.moduleGraph &&
      this.moduleGraph.closeNonPersistingSession(elementInfo?.accessInfo);
  }

  //==========================
  // CALCULATIONS
  //==========================

  async getInitialLiveResults(elementInfo, targetList, messageCallback) {
    let result = await this.moduleGraph.getInitialLiveResults(
      elementInfo,
      targetList
    );
    if (messageCallback) {
      messageCallback(result);
    }
    return result;
  }

  async setUpdatedLiveValues(
    elementInfo,
    targetList,
    targetLookups,
    messageCallback
  ) {
    let result = await this.moduleGraph.setUpdatedLiveValues(
      elementInfo,
      targetList
    );
    if (messageCallback) {
      messageCallback(result?.targets);
    }
    return result;
  }

  async getUpdatedLiveResults(
    elementInfo,
    targetList,
    targetLookups,
    messageCallback
  ) {
    let result = await this.moduleGraph.getUpdatedLiveResults(
      elementInfo,
      targetList,
      targetLookups
    );
    if (messageCallback) {
      messageCallback(result?.targets);
    }
    return result;
  }

  async getLiveCellAddressValue(elementInfo, address, messageCallback) {
    let result = await this.moduleGraph.getLiveCellAddressValue(
      elementInfo,
      address
    );
    if (messageCallback) {
      messageCallback(result);
    }
    return result;
  }

  async setLiveCellArray(
    elementInfo,
    array,
    targetCell,
    fields,
    messageCallback
  ) {
    let result = await this.moduleGraph.setLiveCellValues(
      elementInfo,
      array,
      targetCell,
      fields
    );
    if (messageCallback) {
      messageCallback(result);
    }
    return result;
  }

  async sendChangesAndGetLiveResults(
    elementInfo,
    changesList,
    changesListPrior,
    messageCallback,
    callback
  ) {
    let result = await this.moduleGraph.sendChangesAndGetLiveResults(
      elementInfo,
      changesList,
      changesListPrior
    );
    if (messageCallback) {
      messageCallback(result, callback);
    }
    return result;
  }

  async getRangesLiveResults(elementInfo, rangesList, callback) {
    let result = await this.moduleGraph.getLiveRangeResults(
      elementInfo,
      rangesList,
      callback
    );
    if (callback) {
      callback(result);
    }
    // return result;
  }

  // =======================================================
  // optimize Definition for Range
  // =======================================================

  getChartRefs(chart) {
    let refs = [];
    try {
      let chartGroups = chart?.chartSpace?.chart?.plotArea?.chartGroups ?? [];
      chartGroups.forEach((chartGroup) => {
        let series = chartGroup?.ser ?? [];
        series.forEach((seriesItem) => {
          if (seriesItem?.val?.numRef?.f) {
            let ref = seriesItem.val.numRef.f;
            refs.push(ref);
          }
          if (seriesItem?.cat?.strRef?.f) {
            let ref = seriesItem.cat.strRef.f;
            refs.push(ref);
          }
          if (seriesItem?.tx?.strRef?.f) {
            let ref = seriesItem.tx.strRef.f;
            refs.push(ref);
          }
        });
      });
    } catch (error) {
      console.log("ERROR getChartRefs", error);
    }
    return refs;
  }

  getDefinitionForRange(spread, rangeAddress, options = []) {
    let range = rangeAddress;
    let tryOptimize = true;
    if (options?.includes("noOptimization")) tryOptimize = false;
    const removeShapes = options?.includes("noShapes") ? true : false;
    if (!range || !spread) return;

    range = range.replaceAll("'", "").replaceAll("=", "");
    let offset = getRangeAsOffset(range);
    let sheetName = getSheetNameFromAddress(range);
    if (!sheetName) return;
    sheetName = sheetName.replaceAll("'", "");
    // row and column count
    let sheet = spread.getSheetFromName(sheetName);
    let sheetRowCount = sheet?.getRowCount();
    let sheetColumnCount = sheet?.getColumnCount();

    let data = jsonCopy(spread.toJSON());
    // const fullSize = getObjectSize(data);
    let sheets = data.sheets;
    let activeSheetIndex = 0;
    let uniqueNamedStyles = [];

    Object.keys(sheets).forEach((sheetItem) => {
      if (sheetItem !== sheetName) {
        let sheet = sheets[sheetItem];
        // reduce the size of the sheet
        removeSheetProperties(sheet, false);

        sheets[sheetItem].isSelected = false;
      } else {
        let sheet = sheets[sheetItem] ?? {};
        activeSheetIndex = sheet.index;
        sheet.isSelected = true;
        // remove charts
        // if (sheet.hasOwnProperty("shapes")) delete sheet.shapes;
        if (removeShapes && sheet.hasOwnProperty("shapes")) sheet.shapes = [];
        if (sheet.hasOwnProperty("selections")) sheet.selections = {};
        // only delete charts with external references
        if (sheet.hasOwnProperty("charts")) {
          let newCharts = [];
          const charts = sheet.charts;
          charts.forEach((chart) => {
            let refs = this.getChartRefs(chart) ?? [];
            let hasExternalData = false;
            refs.forEach((ref) => {
              if (!ref?.includes(sheetName)) hasExternalData = true;
            });
            hasExternalData = false; // todo: optimize
            if (!hasExternalData) {
              let isOK = !offset;
              if (offset && chart) {
                if (
                  chart.startColumn >= offset.colFrom &&
                  chart.endColumn <= offset.colTo &&
                  chart.startRow >= offset.rowFrom &&
                  chart.endRow <= offset.rowTo
                ) {
                  isOK = true;
                }
              }
              if (isOK === true && chart) {
                newCharts.push(chart);
              }
            }
          });

          sheet.charts = newCharts;
        }
        // cut out the sheet data
        if (offset) {
          let gridData = getDataGridFromRange(
            sheet.data.dataTable,
            offset.rowFrom,
            offset.colFrom,
            offset.rowTo + 1,
            offset.colTo + 1
          );
          let newData = gridData?.dataTable;
          let uniqueStyles = gridData?.uniqueStyles ?? [];
          if (uniqueStyles.length > 0) uniqueNamedStyles = uniqueStyles;
          if (newData) sheet.data.dataTable = newData;
          // hide the columns
          if (!sheet.columns) sheet.columns = [];
          let columnCount = sheetColumnCount ?? sheet.columnCount;
          for (let i = 0; i <= columnCount; i++) {
            const isHidden = i < offset.colFrom || i > offset.colTo;
            let column = sheet.columns[i];
            if (column && isHidden) column.visible = !isHidden;
            if (!column && isHidden) sheet.columns[i] = { visible: !isHidden };
          }

          // sheet.columns = sheet.columns?.slice(0, offset.colTo + 1);
          // sheet.columns?.forEach((column, index) => {
          //   if (index < offset.colFrom || index > offset.colTo + 1) {
          //     if (column) column.visible = false;
          //     if (!column) sheet.columns[index] = { visible: false };
          //   }
          // });
          // sheet.columnCount = sheet.columns?.length ?? 0;

          // hide the rows
          let rowCount = sheetRowCount ?? sheet.rowCount;
          // if (!rowCount) {
          //   const rows = sheet.data?.dataTable;
          //   rows.forEach((row, index) => {
          //     rowCount = index;
          //   });
          // }
          if (!sheet.rows) sheet.rows = [];
          for (let i = 0; i <= rowCount; i++) {
            const isHidden = i < offset.rowFrom || i > offset.rowTo;
            let row = sheet.rows[i];
            if (row && isHidden) row.visible = !isHidden;
            if (!row) sheet.rows[i] = { visible: !isHidden };
          }
          // if (sheet.rows) {
          // sheet.rows = sheet.rows?.slice(0, offset.rowTo + 1);
          // sheet.rows?.forEach((row, index) => {
          //   if (index < offset.rowFrom || index > offset.rowTo + 1) {
          //     if (row) row.visible = false;
          //     if (!row) sheet.rows[index] = { visible: false };
          //   }
          // });
          // sheet.rowCount = sheet.rows?.length ?? 0;
          // }

          sheet.activeCol = 0;
          sheet.activeRow = 0;
        }
      }
    });
    data.activeSheetIndex = activeSheetIndex;
    // optimized named styles
    // uniqueNamedStyles = getUniqueNamedStylesForRange(
    //   sheets[sheetName],
    //   offset,
    // );
    try {
      if (tryOptimize && uniqueNamedStyles.length > 0) {
        let allStyles = data.namedStyles;
        let namedStyles = [];
        allStyles?.forEach((style, index) => {
          let name = style.name;
          let foundIndex = uniqueNamedStyles.findIndex((x) => x === name);
          if (foundIndex !== -1 || !name.includes("built")) {
            namedStyles.push(style);
          }
        });
        data.namedStyles = namedStyles;
      }
      if (data.docProps?.docPropsApp?.TitlesOfParts?.sheetsNameArray) {
        data.docProps.docPropsApp.TitlesOfParts.sheetsNameArray = [];
      }
    } catch (error) {
      console.log("ERROR getDefinitionForRange", error);
    }

    // =============================================
    // get the external references (outside the main range)
    // =============================================

    // used charts & validations
    // let chartRanges = this.getDataRangesForCharts(sheets[sheetName].charts);
    // let validationRanges = getDataRangesForDropdowns(
    //   sheets[sheetName].validations,
    //   sheetName,
    // );
    // let ranges = [...chartRanges, ...validationRanges];

    let ranges = [];
    // chartRanges.forEach((item) => {
    //   let range = item.range;
    //   const foundIndex = ranges.findIndex((value) => value.range === range);
    //   if (foundIndex === -1) ranges.push(item);
    // });
    // validationRanges.forEach((item) => {
    //   let range = item.range;
    //   const foundIndex = ranges.findIndex((value) => value.range === range);
    //   if (foundIndex === -1) ranges.push(item);
    // });
    // console.log("INFO getDefinitionForRange ~ data:", data)
    const rangeSize = getObjectSize(data);
    // console.log("Size of full json", fullSize);
    console.log("Size of data", rangeSize);

    return { definition: data, externalRanges: ranges };
  }
}
